import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth, API, api } from "@/lib/auth";
import { AI_PROMPTS_BY_CONTEXT } from "@/data/demo";
import { Icon } from "@/components/kit";
import { toast } from "sonner";

const MODES = [
  { key: "assistant", label: "Assistant", icon: "Sparkles" },
  { key: "content", label: "Content", icon: "Megaphone" },
  { key: "market", label: "Explain Market", icon: "TrendingUp" },
  { key: "compliance", label: "Compliance", icon: "ShieldCheck" },
];

export default function AIAssistant() {
  const { refreshUser } = useAuth();
  const [mode, setMode] = useState("assistant");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [session, setSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const streamRef = useRef(0);
  const inputRef = useRef(null);
  const autoRan = useRef(false);
  const location = useLocation();
  const [ctx] = useState(location.state?.context || "business");
  const prompts = AI_PROMPTS_BY_CONTEXT[ctx] || AI_PROMPTS_BY_CONTEXT.business;
  const ctxLabel = ctx === "community" ? "Community" : ctx === "learn" ? "Learning" : null;

  const loadSessions = useCallback(() => api.get("/ai/sessions").then((r) => setSessions(r.data)).catch(() => {}), []);
  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [messages, streaming]);

  const stopStream = () => { streamRef.current += 1; try { abortRef.current?.abort(); } catch (e) { if (e?.name !== "AbortError") console.error("stopStream abort failed", e); } setStreaming(false); };
  const newChat = () => { stopStream(); setSession(null); setMessages([]); setShowSidebar(false); };
  const uid = () => Math.random().toString(36).slice(2);
  const editMessage = (content) => { setInput(content); setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 0); };
  const openSession = async (sid) => {
    stopStream();
    const { data } = await api.get(`/ai/sessions/${sid}`);
    setSession(sid); setMessages(data.map((m) => ({ role: m.role, content: m.content, id: uid() }))); setShowSidebar(false);
  };
  const delSession = async (sid, e) => {
    e.stopPropagation();
    await api.delete(`/ai/sessions/${sid}`); loadSessions();
    if (sid === session) newChat();
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || streaming) return;
    const myId = ++streamRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q, id: uid() }, { role: "assistant", content: "", id: uid() }]);
    setStreaming(true);
    try {
      const doFetch = () => fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q, session_id: session, mode }),
        signal: controller.signal,
      });
      let res = await doFetch();
      if (res.status === 401) {
        try { await api.post("/auth/refresh"); res = await doFetch(); } catch (e) { /* fall through */ }
      }
      if (res.status === 402) { toast.error("Insufficient wallet balance. Please top up."); setMessages((m) => m.slice(0, -1)); setStreaming(false); return; }
      if (!res.ok) { toast.error("AI service error."); setMessages((m) => m.slice(0, -1)); setStreaming(false); return; }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done || streamRef.current !== myId) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const data = JSON.parse(p.slice(6));
          if (streamRef.current !== myId) break;
          if (data.session_id) setSession(data.session_id);
          if (data.delta) setMessages((m) => {
            if (m.length === 0) return m;
            const c = [...m];
            const last = c[c.length - 1];
            c[c.length - 1] = { ...last, content: last.content + data.delta };
            return c;
          });
        }
      }
      if (streamRef.current === myId) { await refreshUser(); loadSessions(); }
    } catch (e) { if (e.name !== "AbortError") toast.error("AI service error."); }
    finally { if (streamRef.current === myId) setStreaming(false); }
  };

  useEffect(() => {
    const p = location.state?.prompt;
    if (p && !autoRan.current) {
      autoRan.current = true;
      send(p);
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  return (
    <div className="fade-up">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => setShowSidebar(true)} className="lg:hidden text-gray-500" data-testid="ai-history-toggle"><Icon name="History" size={22} /></button>
        <div className="w-12 h-12 rounded-2xl grad-blue-violet grid place-items-center"><Icon name="Sparkles" size={24} className="text-white" /></div>
        <div>
          <div className="flex items-center gap-2 flex-wrap"><h1 className="font-head text-2xl md:text-3xl font-bold">MFDX AI Copilot</h1><span className="text-[11px] bg-lime-50 text-lime-700 rounded-full px-2 py-0.5 font-semibold">₹2 / message</span><span className="text-[11px] bg-emerald-500/12 text-emerald-600 border border-emerald-500/20 rounded-full px-2 py-0.5 font-semibold inline-flex items-center gap-1" data-testid="ai-model-badge"><Icon name="Sparkles" size={11} /> Powered by Gemini · 3.1 Pro</span></div>
          <p className="text-gray-500 text-sm">Your AI co-pilot — understands your business, clients, market & compliance.</p>
        </div>
      </div>

      <div className="flex gap-4" style={{ height: "70vh" }}>
        {/* Sessions sidebar */}
        <div className={`${showSidebar ? "fixed inset-0 z-[80] bg-black/40 lg:static lg:bg-transparent lg:z-auto" : "hidden"} lg:block lg:w-64 shrink-0`} onClick={() => setShowSidebar(false)}>
          <div className="card-mfx p-3 h-full w-64 lg:w-full flex flex-col bg-white" onClick={(e) => e.stopPropagation()}>
            <button onClick={newChat} data-testid="ai-new-chat-btn" className="flex items-center gap-2 w-full px-3 py-2.5 rounded-xl grad-blue-violet text-white text-sm font-semibold hover-lift mb-3">
              <Icon name="Plus" size={16} /> New chat
            </button>
            <div className="text-[11px] uppercase tracking-widest text-gray-400 font-semibold px-2 mb-1">History</div>
            <div className="flex-1 overflow-y-auto no-scrollbar space-y-1">
              {sessions.length === 0 && <p className="text-xs text-gray-400 px-2 py-4">No conversations yet. Ask something to start.</p>}
              {sessions.map((s) => (
                <div key={s.session_id} onClick={() => openSession(s.session_id)} data-testid="ai-session-item"
                  className={`group flex items-center gap-2 px-3 py-2 rounded-xl text-sm cursor-pointer ${session === s.session_id ? "bg-blue-50 text-blue-600" : "text-gray-600 hover:bg-gray-50"}`}>
                  <Icon name="MessageSquare" size={14} className="shrink-0" />
                  <span className="truncate flex-1">{s.title}</span>
                  <button onClick={(e) => delSession(s.session_id, e)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500"><Icon name="Trash2" size={13} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Chat */}
        <div className="flex-1 min-w-0 card-mfx overflow-hidden flex flex-col">
          <div className="flex gap-2 p-3 border-b border-gray-100 overflow-x-auto no-scrollbar">
            {MODES.map((m) => (
              <button key={m.key} onClick={() => setMode(m.key)} data-testid={`ai-mode-${m.key}`}
                className={`px-3.5 py-1.5 rounded-xl text-sm font-medium whitespace-nowrap inline-flex items-center gap-1.5 border transition-colors ${mode === m.key ? "grad-blue-violet text-white border-transparent" : "bg-white border-gray-200 text-gray-600"}`}>
                <Icon name={m.icon} size={15} /> {m.label}
              </button>
            ))}
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 bg-[#0A0A0A]">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="w-14 h-14 rounded-2xl grad-blue-violet grid place-items-center mb-4"><Icon name="Sparkles" size={26} className="text-white" /></div>
                <h3 className="font-head text-xl font-semibold text-white">Ask me anything about your MFD business</h3>
                <p className="text-gray-400 text-sm mt-1 mb-6 max-w-md">I can analyse clients, draft messages, explain markets, generate content and decode regulations.</p>
                {ctxLabel && (
                  <div className="mb-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-blue-300 bg-blue-500/10 border border-blue-400/20 rounded-full px-3 py-1" data-testid="ai-context-chip">
                    <Icon name="Sparkles" size={12} /> Showing {ctxLabel} suggestions
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-2 max-w-2xl w-full">
                  {prompts.map((p) => (
                    <button key={p} onClick={() => send(p)} data-testid="ai-example-prompt"
                      className="text-left text-sm text-gray-200 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-3.5 py-2.5 transition-colors">{p}</button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4 max-w-3xl mx-auto">
                {messages.map((m, i) => (
                  <div key={m.id} className={`group flex gap-2 items-center ${m.role === "user" ? "justify-end" : "gap-3"}`}>
                    {m.role === "assistant" && <div className="w-8 h-8 rounded-lg grad-blue-violet grid place-items-center shrink-0 mt-0.5 self-start"><Icon name="Sparkles" size={15} className="text-white" /></div>}
                    {m.role === "user" && !streaming && (
                      <button onClick={() => editMessage(m.content)} data-testid="ai-edit-msg" title="Edit & resend"
                        className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-white transition-opacity shrink-0">
                        <Icon name="Pencil" size={14} />
                      </button>
                    )}
                    <div className={`rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "grad-blue-violet text-white" : "bg-white/5 text-gray-100 border border-white/10"}`} style={{ maxWidth: "80%" }}>
                      {m.content || <span className="text-gray-500">Thinking<span className="cursor-blink">▋</span></span>}
                      {m.role === "assistant" && m.content && streaming && i === messages.length - 1 && <span className="cursor-blink">▋</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="p-3 border-t border-gray-100 bg-white">
            <div className="flex items-center gap-2">
              <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="ai-input"
                placeholder="Message MFDX AI…" className="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-blue-400" />
              <button onClick={() => send()} disabled={streaming} data-testid="ai-send-btn" className="w-11 h-11 rounded-xl grad-blue-violet grid place-items-center text-white disabled:opacity-50">
                <Icon name="Send" size={18} />
              </button>
            </div>
            <div className="flex items-center gap-2 mt-2 text-[11px] text-gray-400"><Icon name="ShieldAlert" size={12} /> AI-generated · assistive only. Not investment advice. Market data is illustrative demo data.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
